/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

/**
 * Component that creates a KPI Info List
 * required params:
 *  - width
 *  - height
 *  - settingsOpenHandler
 *  - canOpenSettings
 *  - label
 *
 */

import React from "react";
import PropTypes from "prop-types";
import { ImageButton, Text } from "vr-react-components";
import { useSelector } from "react-redux";

function KPIWidgetFrontPanel({
    isAlert,
    width,
    height,
    alertColor = "#F00",
    label,
    fontColor,
    value,
    settingsOpenerHandler,
    canOpenSettings,
    maxWidth,
    tile,
    hideSettings,
    ...otherAttributes
}) {
    const canModify = useSelector((state) => state.private.device.canModify);

    const alertWidth = maxWidth / (tile ? 32 : 16);

    return (
        <a-entity {...otherAttributes}>
            {isAlert && value !== null && (
                <a-plane
                    width={alertWidth}
                    height={height}
                    position={`${width / -2 + alertWidth / 2} 0 ${height / 100}`}
                    color={alertColor}
                />
            )}
            <Text
                position={`${-width / 2 + maxWidth / 12} ${
                    tile ? 0 : height / 2 - maxWidth / 20
                } 0.005`}
                text={label}
                align="left"
                baseline={tile ? "center" : "top"}
                width={width * 0.5}
                sizeDecimal={30 / maxWidth}
            />
            <Text
                color={isAlert && value !== null ? alertColor : null}
                position={`${width / 2 - maxWidth / 4} ${
                    tile ? 0 : -height / 2 + height / 5
                } 0.005`}
                text={value === null ? "-" : !value ? "0" : value }
                align="right"
                width={width / 4}
                sizeDecimal={20 / maxWidth}
            />
            {value === null && (
                <Text
                    position={`${width / 2 - maxWidth / 4} ${
                        tile ? -height / 4 : -height / 2 + height / 5
                    } 0.005`}
                    text="No data"
                    align="right"
                    width={width / 4}
                    sizeDecimal={50 / maxWidth}
                />
            )}
            {!hideSettings && (
                <ImageButton
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${width / 2 - maxWidth / 17} 0.05 0.01`}
                    width={maxWidth / 15}
                    height={maxWidth / 15}
                    onClickHandler={settingsOpenerHandler}
                    src="#Settings"
                    disabled={!canOpenSettings}
                />
            )}
        </a-entity>
    );
}

// requiring data
KPIWidgetFrontPanel.propTypes = {
    label: PropTypes.string.isRequired,
    canOpenSettings: PropTypes.bool.isRequired,
    width: PropTypes.PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    height: PropTypes.PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    isAlert: PropTypes.bool.isRequired,
};

export default KPIWidgetFrontPanel;
